/**
 * Rangement des compteurs d'un bâtiment dans les filtres de la page compteurs
 * (`PageBuildingMeter.vue`) : « pdl » (Électricité), « pce » (Gaz), « rcu »
 * (Réseau urbain, compteurs de chaleur), « eau » (Eau).
 *
 * Un sous-compteur de l'Université de Bordeaux (`ubordeaux`, DEV-2100) porte
 * élec, eau ou chaleur sous un même idtype : il se range selon le
 * `compteur.fluide` servi par dapi. Fluide null (ou informations pas encore
 * chargées) : aucune catégorie, il n'apparaît que sous « Tout ».
 */
import { fluideUBordeaux } from "@core/components/source/meter/display/card/meter-ubordeaux.helper"
import type { InformationUBordeaux } from "@types"

export type CategorieCompteurBatiment = "pdl" | "pce" | "rcu" | "eau"

const CATEGORIE_PAR_TYPE: Record<string, CategorieCompteurBatiment> = {
    pdl: "pdl",
    elecsavoie: "pdl",
    elecstrasbourg: "pdl",
    pce: "pce",
    regaz: "pce",
    cpcu: "rcu",
    dalkia: "rcu",
    sigdu: "eau",
}

const CATEGORIE_PAR_FLUIDE: Record<string, CategorieCompteurBatiment> = {
    elec: "pdl",
    eau: "eau",
    chaleur: "rcu",
}

type CompteurRangeable = {
    type: string
    informations?: { ubordeaux?: InformationUBordeaux | null } | null
}

/** Catégorie de filtre d'un compteur, ou null s'il n'en a aucune. */
export function categorieCompteurBatiment(
    source: CompteurRangeable
): CategorieCompteurBatiment | null {
    if (source.type === "ubordeaux") {
        const fluide = fluideUBordeaux(source.informations?.ubordeaux)
        return fluide ? CATEGORIE_PAR_FLUIDE[fluide] : null
    }
    return CATEGORIE_PAR_TYPE[source.type] ?? null
}

/** Compteurs d'une catégorie ; « all » (ou toute autre valeur) les garde tous. */
export function compteursDeCategorie<T extends CompteurRangeable>(
    compteurs: T[],
    categorie: string
): T[] {
    if (!Object.values(CATEGORIE_PAR_TYPE).includes(categorie as never)) {
        return compteurs
    }
    return compteurs.filter(
        (source) => categorieCompteurBatiment(source) === categorie
    )
}
